import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import * as pdfjsLib from "pdfjs-dist";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import {
  hasDbEditorUpdateAccessFromStorage,
} from "../../utils/authAccess";
import LoadingSpinner from "../loadingSpinner/LoadingSpinner";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import "./imageSection.css";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export default function ImageSection({
  data = [],
  setSelectedTab,
  setPricingData,
  setImageData,
  modelNumber,
  documentId,
}) {
  const [pdfData, setPdfData] = useState(null);
  const [filterType, setFilterType] = useState("description");
  const [loadingPdf, setLoadingPdf] = useState(false);
  const canvasContainerRef = useRef(null);

  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1.2);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  const [dropdownType, setDropdownType] = useState("title");
  const [selectedPage, setSelectedPage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const [selectedRows, setSelectedRows] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    keySuffix: "",
    partNumber: "",
    description: ""
  });

  const [isAddPartModalOpen, setIsAddPartModalOpen] = useState(false);
  const [addPartLoading, setAddPartLoading] = useState(false);
  const [addPartError, setAddPartError] = useState("");
  const [addPartPageId, setAddPartPageId] = useState("");
  const [addPartKeyId, setAddPartKeyId] = useState("");
  const [addPartKeySuffixId, setAddPartKeySuffixId] = useState("");
  const [addPartDivId, setAddPartDivId] = useState("");
  const [addPartSourceId, setAddPartSourceId] = useState("");
  const [addPartNumber, setAddPartNumber] = useState("");
  const [addPartTitle, setAddPartTitle] = useState("");
  const [divisions, setDivisions] = useState([]);
  const [loadingDivisions, setLoadingDivisions] = useState(false);

  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [isModifyPageRecordModalOpen, setIsModifyPageRecordModalOpen] = useState(false);
  const [modifyPageRecordLoading, setModifyPageRecordLoading] = useState(false);
  const [modifyPageRecordError, setModifyPageRecordError] = useState("");
  const [modifyPageRecordNewName, setModifyPageRecordNewName] = useState("");

  const [isModifyPageSubjectModalOpen, setIsModifyPageSubjectModalOpen] = useState(false);
  const [modifyPageSubjectLoading, setModifyPageSubjectLoading] = useState(false);
  const [modifyPageSubjectError, setModifyPageSubjectError] = useState("");
  const [modifyPageSubjectId, setModifyPageSubjectId] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  const groupedData = useMemo(() => {
    const groups = {};
    data.forEach((item) => {
      const normalizedPageId = String(item.pageId || "").padStart(5, "0");
      if (!groups[normalizedPageId]) groups[normalizedPageId] = [];
      groups[normalizedPageId].push(item);
    });
    return groups;
  }, [data]);

  const dropdownOptions = useMemo(() => {
    const otherPages = Object.keys(groupedData)
      .filter(pageId => pageId !== "00000")
      .sort((a, b) => Number(a) - Number(b))
      .map((pageId) => ({
        pageId,
        displayPageId: pageId,
        pageTitle: groupedData[pageId][0]?.pageName || `Page ${pageId}`,
        pageSubject: groupedData[pageId][0]?.subject || groupedData[pageId][0]?.subjectName || "",
      }));
    
    return otherPages;
  }, [groupedData]);

  useEffect(() => {
    if (dropdownOptions.length > 0) {
      setSelectedPage(dropdownOptions[0]);
      setCurrentIndex(0);
    }
  }, [dropdownOptions]);

  const formatKeySuffix = (keyId, suffixId) => {
    const paddedKey = String(keyId || "").padStart(6, "0");
    const suffix = suffixId && suffixId.trim() !== "" ? suffixId : "NA";
    return `${paddedKey} ${suffix}`;
  };

  const normalizePageIdForDisplay = (pageId) => {
    const strId = String(pageId).padStart(5, "0");
    return strId === "00000" ? "00001" : strId;
  };

  const currentTableData = useMemo(() => {
    if (!selectedPage) return [];
    
    const normalizedSelectedPageId = String(selectedPage.pageId || "").padStart(5, "0");
    const selectedPageData = groupedData[normalizedSelectedPageId] || [];
    
    const page00000Data = groupedData["00000"] || [];
    
    if (normalizedSelectedPageId === "00000") {
      return page00000Data;
    }
    
    return [...page00000Data, ...selectedPageData];
  }, [selectedPage, groupedData]);

  const filteredTableData = useMemo(() => {
    return currentTableData.filter((item) => {
      const itemNumber = item.number || item.partNumber;
      const itemTitle = item.title || item.partTitle;
      
      if (!itemNumber) return false;
      
      const partNumber = (itemNumber || "").toLowerCase();
      const description = (itemTitle || "").toLowerCase();
      const keySuffix = formatKeySuffix(item.keyId, item.keySuffixId).toLowerCase();
      
      const keySuffixMatch = !searchFilters.keySuffix.trim() || 
        keySuffix.includes(searchFilters.keySuffix.toLowerCase().trim());
      
      const partNumberMatch = !searchFilters.partNumber.trim() || 
        partNumber.includes(searchFilters.partNumber.toLowerCase().trim());
      
      const descriptionMatch = !searchFilters.description.trim() || 
        description.includes(searchFilters.description.toLowerCase().trim());
      
      return keySuffixMatch && partNumberMatch && descriptionMatch;
    });
  }, [currentTableData, searchFilters]);

  const fetchPdfFromBackend = useCallback(
    async (pageId) => {
      if (!data.length || !pageId) {
        setPdfData(null);
        setPdfDocument(null);
        setPdfError(null);
        return;
      }
      try {
        setLoadingPdf(true);
        setPdfError(null);
        const normalizePageId = (pageId) => {
          const strId = String(pageId).padStart(5, "0");
          return strId === "00000" ? "00001" : strId;
        };
        const pageNo = normalizePageId(pageId);
        const key = `processedDocs/${data[0]?.documentTypeId}/${data[0]?.documentId}-${pageNo}.pdf`;

        const arrayBuffer = await fetchWithAuth({
          method: "GET",
          endpoint: "/images/getPdf",
          params: { key },
          responseType: "arraybuffer",
        });

        setPdfData(arrayBuffer || null);
      } catch (err) {
        console.error("Error fetching PDF:", err);
        setPdfData(null);
        setPdfDocument(null);
        setPdfError("Failed to load PDF. Please try again.");
      } finally {
        setLoadingPdf(false);
      }
    },
    [data]
  );

  useEffect(() => {
    if (selectedPage?.pageId) {
      fetchPdfFromBackend(selectedPage.pageId);
    }
  }, [selectedPage, fetchPdfFromBackend]);

  useEffect(() => {
    if (!pdfData) {
      setPdfDocument(null);
      setTotalPages(0);
      setCurrentPage(1);
      return;
    }

    const loadPdfDocument = async () => {
      try {
        setPdfError(null);
        const loadingTask = pdfjsLib.getDocument({ data: pdfData });
        const pdf = await loadingTask.promise;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
        setCurrentPage(1);
      } catch (err) {
        console.error("Error loading PDF:", err);
        setPdfError("Failed to load PDF document.");
        setPdfDocument(null);
      }
    };

    loadPdfDocument();
  }, [pdfData]);

  useEffect(() => {
    if (!pdfDocument || !canvasContainerRef.current) return;

    const renderCurrentPage = async () => {
      try {
        const container = canvasContainerRef.current;
        if (!container) return;
        container.innerHTML = "";

        const page = await pdfDocument.getPage(currentPage);
        const viewport = page.getViewport({ scale: zoomLevel });

          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");
          canvas.height = viewport.height;
          canvas.width = viewport.width;
        canvas.style.maxWidth = "100%";
        canvas.style.height = "auto";
        canvas.style.border = "1px solid #ddd";
        canvas.style.borderRadius = "4px";
        canvas.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";

          await page.render({ canvasContext: context, viewport }).promise;
          container.appendChild(canvas);
      } catch (err) {
        console.error("Error rendering PDF page:", err);
        setPdfError("Failed to render PDF page.");
      }
    };

    renderCurrentPage();
  }, [pdfDocument, currentPage, zoomLevel]);

  const goToPrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const zoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setZoomLevel(1.2);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };


  const goPrev = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setSelectedPage(dropdownOptions[newIndex]);
    }
  };
  const goNext = () => {
    if (currentIndex < dropdownOptions.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setSelectedPage(dropdownOptions[newIndex]);
    }
  };

  useEffect(() => {
    if (!selectedPage) return;
    const idx = dropdownOptions.findIndex(
      (opt) => opt.pageId === selectedPage.pageId
    );
    if (idx !== -1) setCurrentIndex(idx);
  }, [selectedPage, dropdownOptions]);

  const handleRowClick = (row, index) => {
    const rowNumber = row.number || row.partNumber;
    const rowId = `${row.pageId}-${rowNumber}-${index}`;
    const isSelected = selectedRows.some((r) => r.rowId === rowId);

    if (isSelected) {
      setSelectedRows(selectedRows.filter((r) => r.rowId !== rowId));
    } else {
      setSelectedRows([...selectedRows, { ...row, rowId }]);
    }
  };

  const handlePricing = () => {
    const modelInfo = getModelInfo();
    const modelFromData = data && data.length > 0 ? (data[0]?.model || modelInfo.modelNumber || "X") : (modelInfo.modelNumber || "X");
    
    const transformedData = selectedRows.map((row) => ({
      divId: row.divisionId || "",
      sourceId: row.sourceId || "",
      number: row.partNumber || row.number || "",
      quantity: 1, 
      model: row.model || modelFromData,
      title: row.partTitle || row.title || "",
      id: row.rowId || Date.now().toString(), 
    }));
    
    setSelectedTab("PRICING");
    setPricingData(transformedData);
  };

  const isAdmin = hasDbEditorUpdateAccessFromStorage();
  const username = localStorage.getItem("username") || "DBEditorWeb";

  const refetchSourceData = async (modelNum, docId) => {
    if (!modelNum || !docId) {
      return;
    }

    const refreshedPartData = await fetchWithAuth({
      method: "GET",
      endpoint: `/model/getPartAndPagesByModel`,
      params: { modelNumber: modelNum, documentId: docId },
    });

    let enrichedPartData = refreshedPartData;
    if (Array.isArray(refreshedPartData) && refreshedPartData.length > 0 && data && data.length > 0) {
      const modelInfo = data[0];
      enrichedPartData = [...refreshedPartData];
      if (enrichedPartData[0]) {
        enrichedPartData[0] = {
          ...enrichedPartData[0],
          model: modelInfo.model || modelNum || "",
          brndName: modelInfo.brndName || "",
          prdName: modelInfo.prdName || "",
          documentId: docId, 
        };
      }
    } else if (Array.isArray(refreshedPartData) && refreshedPartData.length > 0) {
      enrichedPartData = [...refreshedPartData];
      if (enrichedPartData[0]) {
        enrichedPartData[0] = {
          ...enrichedPartData[0],
          documentId: docId, 
        };
      }
    }

    if (setImageData) {
      setImageData(enrichedPartData);
    }
  };

  const fetchDivisions = async () => {
    setLoadingDivisions(true);
    try {
      const divisionsData = await fetchWithAuth({
        method: "GET",
        endpoint: "/divisions/getAllActiveDivision",
      });
      
      let divisionIds = [];
      
      if (Array.isArray(divisionsData)) {
        divisionIds = divisionsData
          .map((div) => {
            if (typeof div === 'object' && div !== null) {
              return div.id || div.divisionId || div.value || null;
            }
            return div; 
          })
          .filter((id) => id !== null && id !== undefined && id !== '');
      } else if (divisionsData && typeof divisionsData === 'object') {
        const values = Object.values(divisionsData).flat();
        divisionIds = values
          .map((div) => {
            if (typeof div === 'object' && div !== null) {
              return div.id || div.divisionId || div.value || null;
            }
            return div;
          })
          .filter((id) => id !== null && id !== undefined && id !== '');
      }
      
      setDivisions(divisionIds.sort());
    } catch (err) {
      console.error("Error fetching divisions:", err);
      setAddPartError("Failed to load divisions. Please try again.");
    } finally {
      setLoadingDivisions(false);
    }
  };

  const fetchSubjects = async () => {
    setLoadingSubjects(true);
    try {
      const subjectsData = await fetchWithAuth({
        method: "GET",
        endpoint: "/subjects/getAllSubjects",
      });
      
      let subjectsList = [];
      
      if (Array.isArray(subjectsData)) {
        subjectsList = subjectsData.map((subject) => {
          if (typeof subject === 'object' && subject !== null) {
            return {
              id: subject.id || subject.subjectId || subject.value || "",
              name: subject.name || subject.subjectName || subject.label || "",
            };
          }
          return { id: subject, name: subject };
        }).filter((subj) => subj.id !== null && subj.id !== undefined && subj.id !== '');
      } else if (subjectsData && typeof subjectsData === 'object') {
        const values = Object.values(subjectsData).flat();
        subjectsList = values.map((subject) => {
          if (typeof subject === 'object' && subject !== null) {
            return {
              id: subject.id || subject.subjectId || subject.value || "",
              name: subject.name || subject.subjectName || subject.label || "",
            };
          }
          return { id: subject, name: subject };
        }).filter((subj) => subj.id !== null && subj.id !== undefined && subj.id !== '');
      }
      
      setSubjects(subjectsList);
    } catch (err) {
      console.error("Error fetching subjects:", err);
      setModifyPageSubjectError("Failed to load subjects. Please try again.");
    } finally {
      setLoadingSubjects(false);
    }
  };

  const handleAddPart = async () => {
    setIsAddPartModalOpen(true);
    setAddPartError("");
    
    // Set the current page ID from selectedPage (remove leading zeros for display)
    const currentPageId = selectedPage?.pageId || "";
    const displayPageId = currentPageId ? String(currentPageId).replace(/^0+/, "") || currentPageId : "";
    setAddPartPageId(displayPageId);
    
    setAddPartKeyId("");
    setAddPartKeySuffixId("");
    setAddPartDivId("");
    setAddPartSourceId("");
    setAddPartNumber("");
    setAddPartTitle("");
    await fetchDivisions();
  };

  const handleSaveAddPart = async () => {
    setAddPartError("");
    setAddPartLoading(true);

    try {
      if (!addPartPageId.trim() || !addPartKeyId.trim() || !addPartDivId.trim() || !addPartNumber.trim() || !addPartTitle.trim() || !addPartSourceId.trim()) {
        setAddPartError("Please fill in all required fields (Page ID, Key ID, Div ID, Source ID, Number, and Title).");
        setAddPartLoading(false);
        return;
      }

      if (addPartSourceId.trim().length !== 3 || !/^\d{3}$/.test(addPartSourceId.trim())) {
        setAddPartError("Source ID must be exactly 3 digits.");
        setAddPartLoading(false);
        return;
      }

      const currentDocumentId = data && data.length > 0 
        ? (data[0]?.documentId) 
        : null;

      if (!currentDocumentId) {
        setAddPartError("Document ID is required. Please ensure data is loaded.");
        setAddPartLoading(false);
        return;
      }

      const formattedPageId = String(addPartPageId.trim()).padStart(5, "0");
      const keyIdForRequest = String(addPartKeyId.trim()).replace(/^0+/, "") || addPartKeyId.trim();
      
      const payload = {
        pageId: formattedPageId,
        keyId: keyIdForRequest,
        keySuffixId: addPartKeySuffixId.trim() || "",
        divId: addPartDivId.trim(),
        sourceId: addPartSourceId.trim(),
        number: addPartNumber.trim().toUpperCase(),
        title: addPartTitle.trim(),
        documentId: currentDocumentId
      };

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/addPartForModelNumber`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: payload,
      });

      setIsAddPartModalOpen(false);
      setAddPartError("");
      
      setAddPartPageId("");
      setAddPartKeyId("");
      setAddPartKeySuffixId("");
      setAddPartDivId("");
      setAddPartSourceId("");
      setAddPartNumber("");
      setAddPartTitle("");

      const modelNum = modelNumber || (data && data.length > 0 ? data[0]?.model : null);
      if (modelNum && currentDocumentId) {
        await refetchSourceData(modelNum, currentDocumentId);
      }
    } catch (err) {
      console.error("Error adding part:", err);
      let errorMessage = "Failed to add part. Please try again.";
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || JSON.stringify(errorData.error);
        } else if (errorData.errorMessage) {
          errorMessage = errorData.errorMessage;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (typeof errorData === 'object') {
          const stringValues = Object.values(errorData).filter(v => typeof v === 'string' && v.trim());
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setAddPartError(errorMessage);
    } finally {
      setAddPartLoading(false);
    }
  };

  const handleCancelAddPart = () => {
    setIsAddPartModalOpen(false);
    setAddPartError("");
    setAddPartPageId("");
    setAddPartKeyId("");
    setAddPartKeySuffixId("");
    setAddPartDivId("");
    setAddPartSourceId("");
    setAddPartNumber("");
    setAddPartTitle("");
  };

  const handleDeletePart = () => {
    if (selectedRows.length === 0) {
      setDeleteError("Please select at least one part to delete.");
    } else {
      setIsDeleteConfirmModalOpen(true);
      setDeleteError("");
    }
  };

  const handleConfirmDelete = async () => {
    setDeleteLoading(true);
    setDeleteError("");

    try {
      const currentDocumentId = data && data.length > 0 
        ? (data[0]?.documentId || documentId) 
        : documentId;

      const deletePayload = selectedRows.map((row) => {
        const hsin = row.hsin || "";
        const id = row.id || "";
        const pageId = row.pageId || "";
        const keyId = String(row.keyId || "").replace(/^0+/, "") || row.keyId || "";
        const keySuffixId = row.keySuffixId || "";
        const rowDocumentId = row.documentId || currentDocumentId;

        return {
          hsin: hsin,
          id: id,
          pageId: pageId,
          keyId: keyId,
          keySuffixId: keySuffixId,
          documentId: rowDocumentId
        };
      });

      await fetchWithAuth({
        method: "PUT",
        endpoint: `/model/deletePartForModelNumber`,
        params: { username: username },
        headers: { "Content-Type": "application/json" },
        data: deletePayload,
      });

      setIsDeleteConfirmModalOpen(false);
      setSelectedRows([]);
      setDeleteError("");
      
      const modelNum = modelNumber || (data && data.length > 0 ? data[0]?.model : null);
      if (modelNum && currentDocumentId) {
        await refetchSourceData(modelNum, currentDocumentId);
      }
    } catch (err) {
      console.error("Error deleting parts:", err);
      let errorMessage = "Failed to delete parts. Please try again.";
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || JSON.stringify(errorData.error);
        } else if (errorData.errorMessage) {
          errorMessage = errorData.errorMessage;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (typeof errorData === 'object') {
          const stringValues = Object.values(errorData).filter(v => typeof v === 'string' && v.trim());
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setDeleteError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteConfirmModalOpen(false);
    setDeleteError("");
  };

  const handleModifyPageRecord = () => {
    if (!selectedPage) {
      setModifyPageRecordError("Please select a page first.");
      return;
    }
    setIsModifyPageRecordModalOpen(true);
    setModifyPageRecordError("");
    setModifyPageRecordNewName(selectedPage.pageTitle || "");
  };

  const handleSaveModifyPageRecord = async () => {
    setModifyPageRecordError("");
    setModifyPageRecordLoading(true);

    try {
      if (!modifyPageRecordNewName.trim()) {
        setModifyPageRecordError("New name is required.");
        setModifyPageRecordLoading(false);
        return;
      }

      const currentDocumentId = data && data.length > 0 
        ? (data[0]?.documentId || documentId) 
        : documentId;

      if (!currentDocumentId) {
        setModifyPageRecordError("Document ID is required. Please ensure data is loaded.");
        setModifyPageRecordLoading(false);
        return;
      }

      if (!selectedPage?.pageId) {
        setModifyPageRecordError("Page ID is required.");
        setModifyPageRecordLoading(false);
        return;
      }

      const formattedPageId = String(selectedPage.pageId).padStart(5, "0");

      await fetchWithAuth({
        method: "PUT",
        endpoint: "/pages/update-name",
        headers: { "Content-Type": "application/json" },
        data: {
          documentId: currentDocumentId,
          pageId: formattedPageId,
          newName: modifyPageRecordNewName.trim(),
          updatedBy: username,
        },
      });

      setIsModifyPageRecordModalOpen(false);
      setModifyPageRecordError("");
      setModifyPageRecordNewName("");

      const modelNum = modelNumber || (data && data.length > 0 ? data[0]?.model : null);
      if (modelNum && currentDocumentId) {
        await refetchSourceData(modelNum, currentDocumentId);
      }
    } catch (err) {
      console.error("Error modifying page record:", err);
      let errorMessage = "Failed to modify page record. Please try again.";
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || JSON.stringify(errorData.error);
        } else if (errorData.errorMessage) {
          errorMessage = errorData.errorMessage;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (typeof errorData === 'object') {
          const stringValues = Object.values(errorData).filter(v => typeof v === 'string' && v.trim());
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setModifyPageRecordError(errorMessage);
    } finally {
      setModifyPageRecordLoading(false);
    }
  };

  const handleCancelModifyPageRecord = () => {
    setIsModifyPageRecordModalOpen(false);
    setModifyPageRecordError("");
    setModifyPageRecordNewName("");
  };

  const handleModifyPageSubject = async () => {
    if (!selectedPage) {
      setModifyPageSubjectError("Please select a page first.");
      return;
    }
    setIsModifyPageSubjectModalOpen(true);
    setModifyPageSubjectError("");
    setModifyPageSubjectId("");
    await fetchSubjects();
  };

  useEffect(() => {
    if (isModifyPageSubjectModalOpen && subjects.length > 0 && selectedPage) {
      const currentSubject = selectedPage.pageSubject || "";
      if (currentSubject) {
        const matchingSubject = subjects.find(subj => {
          const subjIdTrimmed = String(subj.id || "").trim();
          const subjNameTrimmed = String(subj.name || "").trim();
          const currentTrimmed = String(currentSubject).trim();
          
          return subjIdTrimmed === currentTrimmed || 
                 subjNameTrimmed === currentTrimmed ||
                 subjIdTrimmed === currentTrimmed.padEnd(10, " ") ||
                 subjIdTrimmed === currentTrimmed.padStart(10, " ");
        });
        if (matchingSubject && !modifyPageSubjectId) {
          setModifyPageSubjectId(matchingSubject.id);
        }
      }
    }
  }, [isModifyPageSubjectModalOpen, subjects, selectedPage]);

  const handleSaveModifyPageSubject = async () => {
    setModifyPageSubjectError("");
    setModifyPageSubjectLoading(true);

    try {
      if (!modifyPageSubjectId || String(modifyPageSubjectId).trim() === "") {
        setModifyPageSubjectError("Subject is required. Please select a subject.");
        setModifyPageSubjectLoading(false);
        return;
      }

      const currentDocumentId = data && data.length > 0 
        ? (data[0]?.documentId || documentId) 
        : documentId;

      if (!currentDocumentId) {
        setModifyPageSubjectError("Document ID is required. Please ensure data is loaded.");
        setModifyPageSubjectLoading(false);
        return;
      }

      if (!selectedPage?.pageId) {
        setModifyPageSubjectError("Page ID is required.");
        setModifyPageSubjectLoading(false);
        return;
      }

      const formattedPageId = String(selectedPage.pageId).padStart(5, "0");
      const formattedSubjectId = String(modifyPageSubjectId || "").trim().padEnd(10, " ").substring(0, 10);

      await fetchWithAuth({
        method: "PUT",
        endpoint: "/pages/update-subject",
        headers: { "Content-Type": "application/json" },
        data: {
          documentId: currentDocumentId,
          pageId: formattedPageId,
          subjectId: formattedSubjectId,
          updatedBy: username,
        },
      });

      setIsModifyPageSubjectModalOpen(false);
      setModifyPageSubjectError("");
      setModifyPageSubjectId("");

      const modelNum = modelNumber || (data && data.length > 0 ? data[0]?.model : null);
      if (modelNum && currentDocumentId) {
        await refetchSourceData(modelNum, currentDocumentId);
      }
    } catch (err) {
      console.error("Error modifying page subject:", err);
      let errorMessage = "Failed to modify page subject. Please try again.";
      
      if (err.response?.data) {
        const errorData = err.response.data;
        
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' 
            ? errorData.error 
            : errorData.error.message || JSON.stringify(errorData.error);
        } else if (errorData.errorMessage) {
          errorMessage = errorData.errorMessage;
        } else if (errorData.msg) {
          errorMessage = errorData.msg;
        } else if (typeof errorData === 'object') {
          const stringValues = Object.values(errorData).filter(v => typeof v === 'string' && v.trim());
          if (stringValues.length > 0) {
            errorMessage = stringValues[0];
          } else {
            errorMessage = JSON.stringify(errorData);
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setModifyPageSubjectError(errorMessage);
    } finally {
      setModifyPageSubjectLoading(false);
    }
  };

  const handleCancelModifyPageSubject = () => {
    setIsModifyPageSubjectModalOpen(false);
    setModifyPageSubjectError("");
    setModifyPageSubjectId("");
  };

  const handleFilterChange = (field, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearAllFilters = () => {
    setSearchFilters({
      keySuffix: "",
      partNumber: "",
      description: ""
    });
  };

  const hasActiveFilters = () => {
    return Object.values(searchFilters).some(filter => filter.trim() !== "");
  };

  const getSearchResultsCount = () => {
    const totalItems = currentTableData.filter((item) => item.number || item.partNumber).length;
    const filteredItems = filteredTableData.length;
    return { total: totalItems, filtered: filteredItems };
  };

  const getModelInfo = () => {
    if (!data || data.length === 0) {
      return { productType: "", brandName: "", modelNumber: "" };
    }
    
    const firstItem = data[0];
    if (firstItem?.prdName || firstItem?.brndName || firstItem?.model) {
      return {
        productType: firstItem.prdName || "",
        brandName: firstItem.brndName || "",
        modelNumber: firstItem.model || "",
      };
    }
    
    const itemWithFields = data.find(item => item.prdName || item.brndName || item.model);
    if (itemWithFields) {
      return {
        productType: itemWithFields.prdName || "",
        brandName: itemWithFields.brndName || "",
        modelNumber: itemWithFields.model || "",
      };
    }
    
    return { productType: "", brandName: "", modelNumber: "" };
  };

  const modelInfo = getModelInfo();

  return (
    <div className="imageSectionWrapper">
      {data.length > 0 ? (
        <>
          <div className="topInfoRow">
            <div className="infoItem">
              <h5>Product Type: {modelInfo.productType}</h5>
            </div>
            <div className="infoItem">
              <h5>Brand: {modelInfo.brandName}</h5>
            </div>
            <div className="infoItem">
              <h5>Model: {modelInfo.modelNumber}</h5>
            </div>
          </div>

          <div className="verticalBlocks">
            <div className={`leftBlock ${isFullscreen ? 'fullscreen' : ''}`}>
              {pdfDocument && (
                <div className="pdfControls">
                  <div className="pdfNav">
                    <button 
                      onClick={goToPrevPage} 
                      disabled={currentPage <= 1}
                      title="Previous Page"
                    >
                      ◀
                    </button>
                    <span className="pageInfo">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button 
                      onClick={goToNextPage} 
                      disabled={currentPage >= totalPages}
                      title="Next Page"
                    >
                      ▶
                    </button>
                    {totalPages > 1 && (
                      <input
                        type="number"
                        min="1"
                        max={totalPages}
                        value={currentPage}
                        onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                        className="pageInput"
                        title="Go to page"
                      />
                    )}
                  </div>
                  
                  <div className="zoomControls">
                    <button onClick={zoomOut} title="Zoom Out">-</button>
                    <span className="zoomLevel">{Math.round(zoomLevel * 100)}%</span>
                    <button onClick={zoomIn} title="Zoom In">+</button>
                    <button onClick={resetZoom} title="Reset Zoom">Reset</button>
                  </div>
                  
                  <div className="pdfActions">
                    <button onClick={toggleFullscreen} title="Toggle Fullscreen">
                      {isFullscreen ? '⤓' : '⤢'}
                    </button>
                  </div>
                </div>
              )}

              {loadingPdf && (
                <div className="pdfLoading">
                  <LoadingSpinner />
                  <p>Loading PDF...</p>
                </div>
              )}
              
              {pdfError && (
                <div className="pdfError">
                  <p>❌ {pdfError}</p>
                  <button onClick={() => fetchPdfFromBackend(selectedPage?.pageId)}>
                    Retry
                  </button>
                </div>
              )}
              
              {!loadingPdf && !pdfData && !pdfError && (
                <div className="pdfEmpty">
                  <p>📄 No PDF available</p>
                </div>
              )}
              
              <div 
                ref={canvasContainerRef} 
                className={`pdfCanvasContainer ${isFullscreen ? 'fullscreen' : ''}`}
              />
            </div>

            <div className="rightBlock">
              <div className="filterControls">
                <label>
                  <input
                    type="radio"
                    name="filterType"
                    value="description"
                    checked={filterType === "description"}
                    onChange={(e) => setFilterType(e.target.value)}
                  />
                  Description
                </label>
                <label>
                  <input
                    type="radio"
                    name="filterType"
                    value="type"
                    checked={filterType === "type"}
                    onChange={(e) => setFilterType(e.target.value)}
                  />
                  Type
                </label>
                <span>
                  {filterType === "description"
                    ? data[0]?.documentTitle || ""
                    : data[0]?.documentTypeName || ""}
                </span>
              </div>

              <div className="filterControls">
                <div className="radioGroup">
                  <label>
                    <input
                      type="radio"
                      name="dropdownType"
                      value="title"
                      checked={dropdownType === "title"}
                      onChange={(e) => setDropdownType(e.target.value)}
                    />
                    Title
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="dropdownType"
                      value="pageId"
                      checked={dropdownType === "pageId"}
                      onChange={(e) => setDropdownType(e.target.value)}
                    />
                    Page ID
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="dropdownType"
                      value="subject"
                      checked={dropdownType === "subject"}
                      onChange={(e) => setDropdownType(e.target.value)}
                    />
                    Subject
                  </label>
                </div>

                <select
                  value={selectedPage?.pageId || ""}
                  onChange={(e) => {
                    const opt = dropdownOptions.find(
                      (o) => o.pageId === e.target.value
                    );
                    if (opt) setSelectedPage(opt);
                  }}
                >
                  {dropdownOptions.map((opt) => (
                    <option
                      key={opt.pageId}
                      value={opt.pageId}
                      title={opt.pageTitle}
                    >
                      {dropdownType === "title" 
                        ? opt.pageTitle 
                        : dropdownType === "subject" 
                        ? opt.pageSubject || opt.pageTitle
                        : opt.displayPageId}
                    </option>
                  ))}
                </select>
              </div>

              {hasActiveFilters() && (
                <div className="filterStatusBar">
                  <span className="filterStatusText">
                    Showing {getSearchResultsCount().filtered} of {getSearchResultsCount().total} parts
                  </span>
                  <button onClick={clearAllFilters} className="clearAllFiltersBtn">
                    Clear All Filters
                  </button>
                </div>
              )}

              {filteredTableData.length > 0 ? (
                <div className="scrollableTable">
                  <table
                    className="partsTable"
                  >
                    <thead>
                      <tr>
                        <th></th>
                        <th>Key And Suffix</th>
                        <th>Part Number</th>
                        <th>Description</th>
                      </tr>
                      <tr className="filterRow">
                        <td></td>
                        <td>
                          <input
                            type="text"
                            placeholder="Search Key & Suffix"
                            className="tableSearchInput"
                            value={searchFilters.keySuffix}
                            onChange={(e) => handleFilterChange('keySuffix', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            placeholder="Search Part Number"
                            className="tableSearchInput"
                            value={searchFilters.partNumber}
                            onChange={(e) => handleFilterChange('partNumber', e.target.value)}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            placeholder="Search Description"
                            className="tableSearchInput"
                            value={searchFilters.description}
                            onChange={(e) => handleFilterChange('description', e.target.value)}
                          />
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTableData.map((item, index) => {
                          const itemNumber = item.number || item.partNumber;
                          const itemTitle = item.title || item.partTitle;
                          const rowId = `${item.pageId}-${itemNumber}-${index}`;
                          const isChecked = selectedRows.some(
                            (r) => r.rowId === rowId
                          );
                          return (
                            <tr key={rowId}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={() => handleRowClick(item, index)}
                                />
                              </td>
                              <td>
                                {formatKeySuffix(item.keyId, item.keySuffixId)}
                              </td>
                              <td>{itemNumber}</td>
                              <td>{itemTitle || ""}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="noResultsMessage">
                  {hasActiveFilters() ? (
                    <div>
                      <p>🔍 No parts found matching the current filters</p>
                      <button onClick={clearAllFilters} className="clearFilterBtn">
                        Clear All Filters
                      </button>
                    </div>
                  ) : (
                    <p>📄 No parts available for this selection.</p>
                  )}
                </div>
              )}
              <br />
              <div className="bottomControls">
                {/* First Line: Navigation + Page Management */}
                <div className="navControls">
                  {/* Navigation */}
                  <div className="navSection">
                    <button 
                      onClick={goPrev} 
                      disabled={currentIndex === 0}
                      className="navButton"
                    >
                  ◀
                </button>
                    <span className="pageCounter">
                      {currentIndex + 1}/{dropdownOptions.length}
                </span>
                <button
                  onClick={goNext}
                  disabled={currentIndex === dropdownOptions.length - 1}
                      className="navButton"
                >
                  ▶
                </button>
                </div>

                <div className="partManagementButtons">
                  {isAdmin && (
                    <button
                      className="partButton"
                      onClick={handleAddPart}
                    >
                      <AddIcon /> Add Part
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      className="partButton"
                      disabled={selectedRows.length === 0}
                      onClick={handleDeletePart}
                    >
                      <DeleteIcon /> Delete Part
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      className="partButton"
                      onClick={handleModifyPageRecord}
                    >
                      Modify Page Record
                    </button>
                  )}
                  {isAdmin && (
                    <button
                      className="partButton"
                      onClick={handleModifyPageSubject}
                    >
                      Modify Page Subject
                    </button>
                  )}
                  <button
                    className="partButton pricingButton"
                    disabled={selectedRows.length === 0}
                    onClick={handlePricing}
                  >
                    <MonetizationOnIcon /> Pricing
                  </button>
                </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <p className="noDataMessage">No data available to display</p>
      )}

      {isAddPartModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Add Part</h3>
            {addPartError && <p className="errorText">{addPartError}</p>}
            
            <label>
              Page ID <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={addPartPageId}
                onChange={(e) => setAddPartPageId(e.target.value)}
                disabled={true}
                placeholder="Enter page ID"
                maxLength={5}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </label>

            <label>
              Key ID <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={addPartKeyId}
                onChange={(e) => setAddPartKeyId(e.target.value)}
                disabled={addPartLoading}
                placeholder="Enter key ID"
                maxLength={6}
              />
            </label>

            <label>
              Key Suffix ID (Optional):
              <input
                type="text"
                value={addPartKeySuffixId}
                onChange={(e) => setAddPartKeySuffixId(e.target.value)}
                disabled={addPartLoading}
                placeholder="Enter key suffix ID"
                maxLength={2}
              />
            </label>

            <label>
              Div ID <span style={{ color: 'red' }}>*</span>:
              {loadingDivisions ? (
                <div style={{ padding: "8px", color: "#666" }}>Loading divisions...</div>
              ) : (
                <select
                  value={addPartDivId}
                  onChange={(e) => setAddPartDivId(e.target.value)}
                  disabled={addPartLoading}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select Div ID</option>
                  {divisions.map((divId) => (
                    <option key={divId} value={divId}>
                      {divId}
                    </option>
                  ))}
                </select>
              )}
            </label>

            <label>
              Source ID <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={addPartSourceId}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                  setAddPartSourceId(value);
                }}
                disabled={addPartLoading}
                placeholder="Enter 3-digit source ID"
                maxLength={3}
              />
            </label>

            <label>
              Number <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={addPartNumber}
                onChange={(e) => setAddPartNumber(e.target.value.toUpperCase())}
                disabled={addPartLoading}
                placeholder="Enter part number"
              />
            </label>

            <label>
              Description <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={addPartTitle}
                onChange={(e) => setAddPartTitle(e.target.value)}
                disabled={addPartLoading}
                placeholder="Enter Description"
              />
            </label>

            <div className="modalActions">
              <button onClick={handleSaveAddPart} disabled={addPartLoading}>
                {addPartLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Adding...
                  </>
                ) : (
                  "Add"
                )}
              </button>
              <button onClick={handleCancelAddPart} disabled={addPartLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isDeleteConfirmModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Confirm Delete</h3>
            {deleteError && <p className="errorText">{deleteError}</p>}
            <p>
              Are you sure you want to delete {selectedRows.length} part{selectedRows.length > 1 ? 's' : ''}?
            </p>
            {selectedRows.length > 0 && (
              <div style={{ marginBottom: '16px', maxHeight: '200px', overflowY: 'auto' }}>
                <strong>Parts to be deleted:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  {selectedRows.map((row, index) => (
                    <li key={index} style={{ marginBottom: '4px' }}>
                      {row.partNumber || row.number} 
                      {row.divisionId || row.divId ? ` (Div: ${row.divisionId || row.divId})` : ''}
                      {row.sourceId ? ` (PLS: ${row.sourceId})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <p style={{ color: '#dc3545', fontWeight: 'bold' }}>
              This action cannot be undone.
            </p>
            <div className="modalActions">
              <button onClick={handleConfirmDelete} disabled={deleteLoading} style={{ backgroundColor: '#dc3545', color: 'white' }}>
                {deleteLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Deleting...
                  </>
                ) : (
                  "Delete"
                )}
              </button>
              <button onClick={handleCancelDelete} disabled={deleteLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isModifyPageRecordModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Modify Page Record</h3>
            {modifyPageRecordError && <p className="errorText">{modifyPageRecordError}</p>}
            
            <label>
              Document ID:
              <input
                type="text"
                value={data && data.length > 0 ? (data[0]?.documentId || documentId || "") : (documentId || "")}
                disabled={true}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </label>

            <label>
              Page ID:
              <input
                type="text"
                value={selectedPage?.pageId ? String(selectedPage.pageId).padStart(5, "0") : ""}
                disabled={true}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </label>

            <label>
              New Name <span style={{ color: 'red' }}>*</span>:
              <input
                type="text"
                value={modifyPageRecordNewName}
                onChange={(e) => setModifyPageRecordNewName(e.target.value)}
                disabled={modifyPageRecordLoading}
                placeholder="Enter new page name"
              />
            </label>

            <div className="modalActions">
              <button onClick={handleSaveModifyPageRecord} disabled={modifyPageRecordLoading}>
                {modifyPageRecordLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <button onClick={handleCancelModifyPageRecord} disabled={modifyPageRecordLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {isModifyPageSubjectModalOpen && (
        <div className="modalOverlay">
          <div className="modalContent">
            <h3>Modify Page Subject</h3>
            {modifyPageSubjectError && <p className="errorText">{modifyPageSubjectError}</p>}
            
            <label>
              Document ID:
              <input
                type="text"
                value={data && data.length > 0 ? (data[0]?.documentId || documentId || "") : (documentId || "")}
                disabled={true}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </label>

            <label>
              Page ID:
              <input
                type="text"
                value={selectedPage?.pageId ? String(selectedPage.pageId).padStart(5, "0") : ""}
                disabled={true}
                style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
              />
            </label>

            <label>
              Subject <span style={{ color: 'red' }}>*</span>:
              {loadingSubjects ? (
                <div style={{ padding: "8px", color: "#666" }}>Loading subjects...</div>
              ) : (
                <select
                  value={modifyPageSubjectId}
                  onChange={(e) => setModifyPageSubjectId(e.target.value)}
                  disabled={modifyPageSubjectLoading}
                  style={{
                    width: "100%",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                    fontSize: "14px",
                  }}
                >
                  <option value="">Select Subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                    </option>
                  ))}
                </select>
              )}
            </label>

            <div className="modalActions">
              <button onClick={handleSaveModifyPageSubject} disabled={modifyPageSubjectLoading}>
                {modifyPageSubjectLoading ? (
                  <>
                    <div className="button-spinner"></div>
                    Updating...
                  </>
                ) : (
                  "Update"
                )}
              </button>
              <button onClick={handleCancelModifyPageSubject} disabled={modifyPageSubjectLoading}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}